<template>
  <div class="tw:flex tw:flex-col tw:gap-4" data-testid="candidate-messaging">
    <div v-if="isLoading" class="tw:flex tw:justify-center tw:py-3 tw:text-gray-500">
      <IconLoader2 :size="20" class="tw:animate-spin" :aria-label="$t('common.loading')" />
    </div>
    <ul
      v-else-if="messages.length"
      ref="listElement"
      class="tw:m-0! tw:flex tw:max-h-[420px] tw:list-none tw:flex-col tw:gap-3 tw:overflow-y-auto tw:p-0! tw:pr-1!">
      <li
        v-for="message in messages"
        :key="message.id"
        class="tw:flex tw:max-w-[80%] tw:flex-col tw:gap-1"
        :class="message.direction === 'outbound' ? 'tw:items-end tw:self-end' : 'tw:items-start tw:self-start'">
        <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5 tw:text-12">
          <LeadPill :tone="message.medium === 'email' ? 'primary' : 'neutral'">
            {{ message.medium === 'email' ? $t('candidate.messaging.send_email') : $t('candidate.messaging.send_sms') }}
          </LeadPill>
          <span
            class="tw:font-medium"
            :class="message.direction === 'inbound' ? 'tw:text-primary' : 'tw:text-gray-500'">
            {{
              message.direction === 'inbound'
                ? (props.candidateName ?? $t('candidate.messaging.direction_inbound'))
                : $t('candidate.messaging.direction_outbound')
            }}
          </span>
          <span :class="MESSAGE_STATUS[message.status].textClass">
            {{ $t(MESSAGE_STATUS[message.status].labelKey) }}
          </span>
          <span class="tw:text-gray-400">{{ formatDateTime(message.created_at) }}</span>
        </div>
        <div
          class="tw:flex tw:flex-col tw:gap-1 tw:rounded-[12px] tw:px-3.5 tw:py-2.5 tw:text-13"
          :class="
            message.direction === 'outbound'
              ? 'tw:rounded-br-[4px] tw:bg-primary tw:text-white'
              : 'tw:rounded-bl-[4px] tw:bg-gray-100 tw:text-gray-700'
          ">
          <div v-if="message.subject" class="tw:font-semibold">{{ message.subject }}</div>
          <div class="tw:whitespace-pre-wrap tw:wrap-break-word">{{ message.body }}</div>
        </div>
      </li>
    </ul>
    <p v-else class="text-muted mb-0">{{ $t('candidate.messaging.no_messages') }}</p>

    <CandidateMessagingNotice v-if="props.orderFinished">
      {{ $t('candidate.messaging.order_finished') }}
    </CandidateMessagingNotice>
    <CandidateMessagingNotice v-else-if="!props.availableMediums.length" data-testid="candidate-no-contact">
      {{ $t('candidate.messaging.no_contact') }}
    </CandidateMessagingNotice>
    <div v-else class="tw:flex tw:flex-col tw:gap-3 tw:border-t tw:border-solid tw:border-gray-200 tw:pt-4">
      <LeadTabs v-model="form.medium" variant="boxed" :tabs="mediumTabs(props.availableMediums)" />

      <div>
        <LeadSelect
          :model-value="selectedTemplateKey"
          :options="templateOptions"
          :placeholder="$t('candidate.messaging.template_placeholder')"
          :disabled="props.orderId === null"
          clearable
          @update:model-value="(value) => onTemplateSelected(value as FormMessageTemplateKey | null)" />
        <p class="tw:mt-1.5! tw:mb-0! tw:text-13 tw:text-gray-600">{{ $t('candidate.messaging.template_hint') }}</p>
      </div>

      <div v-if="isLoadingTemplate" class="tw:flex tw:justify-center tw:p-6 tw:text-gray-500">
        <IconLoader2 :size="20" class="tw:animate-spin" :aria-label="$t('common.loading')" />
      </div>

      <template v-else>
        <LeadInput
          v-if="form.medium === 'email'"
          v-model="form.subject"
          :placeholder="$t('candidate.messaging.subject_placeholder')" />

        <LeadTextarea v-model="form.body" :placeholder="$t('candidate.messaging.body_placeholder')" />
      </template>

      <div
        v-if="activeSignature"
        class="tw:flex tw:flex-col tw:gap-0.5 tw:rounded-sm tw:border tw:border-solid tw:border-gray-200 tw:bg-gray-50 tw:px-3 tw:py-2.5 tw:text-12">
        <span class="tw:text-10 tw:font-semibold tw:tracking-wide tw:text-gray-500 tw:uppercase">
          {{ $t('candidate.messaging.signature_label') }}
        </span>
        <span class="tw:whitespace-pre-wrap tw:wrap-break-word tw:text-gray-600">{{ activeSignature }}</span>
      </div>

      <div class="tw:flex tw:justify-end">
        <LeadButton variant="primary" :loading="isSending" :disabled="!canSend" @click="send">
          {{ $t('candidate.messaging.send_button') }}
        </LeadButton>
      </div>
    </div>

    <LeadModal
      v-model="isReplaceConfirmationOpen"
      size="sm"
      :title="$t('candidate.messaging.replace_template_title')"
      :cancel-label="$t('btn.cancel')"
      :confirm-label="$t('candidate.messaging.replace_template_confirm')"
      @confirm="confirmTemplateReplacement"
      @close="pendingTemplateKey = null">
      <p class="tw:m-0! tw:text-14 tw:text-gray-700">{{ $t('candidate.messaging.replace_template_text') }}</p>
    </LeadModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, watch } from 'vue';
import { IconLoader2 } from '@tabler/icons-vue';
import { fetchCandidateMessages, sendCandidateMessage, previewMessageTemplate, getSignature } from '@/api/candidates';
import type {
  FormMessage,
  FormMessageMedium,
  FormMessageStatus,
  FormMessageTemplateKey,
  FormUserSignature,
} from '@/types/candidate';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import LeadInput from '@/components/lead-design/LeadInput.vue';
import LeadModal from '@/components/lead-design/LeadModal.vue';
import LeadPill from '@/components/lead-design/LeadPill.vue';
import LeadSelect, { type LeadSelectOption } from '@/components/lead-design/LeadSelect.vue';
import LeadTabs from '@/components/lead-design/LeadTabs.vue';
import LeadTextarea from '@/components/lead-design/LeadTextarea.vue';
import CandidateMessagingNotice from '@/components/candidate/CandidateMessagingNotice.vue';
import { useCandidateUpdates } from '@/composables/useCandidateUpdates';
import { availableMediumOrFirst, mediumTabs } from '@/helpers/candidateMessaging';
import translator from '@/locale/translator';
import { formatDateTime } from '@/filters/dateFormat';

const MESSAGE_STATUS: Record<FormMessageStatus, { labelKey: string; textClass: string }> = {
  sent: { labelKey: 'candidate.messaging.status_sent', textClass: 'tw:text-success-700' },
  failed: { labelKey: 'candidate.messaging.status_failed', textClass: 'tw:text-danger' },
  pending: { labelKey: 'candidate.messaging.status_pending', textClass: 'tw:text-gray-500' },
};

/* --------------------------------- PROPS ----------------------------------- */

interface Props {
  candidateEntityId: string;
  publicationId: number | null;
  orderId: number | null;
  orderFinished?: boolean;
  candidateName?: string;
  availableMediums: FormMessageMedium[];
}

const props = defineProps<Props>();

/* --------------------------------- STATE ----------------------------------- */

const isLoading = ref(false);
const isSending = ref(false);
const isLoadingTemplate = ref(false);
const messages = ref<FormMessage[]>([]);
const listElement = ref<HTMLElement | null>(null);
const signature = ref<FormUserSignature | null>(null);

const form = ref<{ medium: FormMessageMedium; subject: string; body: string }>({
  medium: 'email',
  subject: '',
  body: '',
});

const selectedTemplateKey = ref<FormMessageTemplateKey | null>(null);
// the text of the last applied template, to tell whether the recruiter has edited it since
const appliedTemplate = ref<{ subject: string; body: string } | null>(null);
const pendingTemplateKey = ref<FormMessageTemplateKey | null>(null);
const isReplaceConfirmationOpen = ref(false);

/* --------------------------------- COMPUTED -------------------------------- */

const templateOptions = computed<LeadSelectOption[]>(() => [
  { value: 'invitation_to_interview', label: translator.get('candidate.messaging.template_invitation_to_interview') },
  { value: 'rejection', label: translator.get('candidate.messaging.template_rejection') },
]);

const activeSignature = computed(() =>
  form.value.medium === 'email' ? (signature.value?.email_signature ?? null) : (signature.value?.sms_signature ?? null)
);

const canSend = computed(
  () =>
    !isLoadingTemplate.value && form.value.body.trim().length > 0 && props.availableMediums.includes(form.value.medium)
);

const hasUnsavedEdits = computed(() => {
  const { subject, body } = form.value;
  if (!subject.trim() && !body.trim()) return false;
  return !appliedTemplate.value || appliedTemplate.value.subject !== subject || appliedTemplate.value.body !== body;
});

/* --------------------------------- METHODS --------------------------------- */

const scrollToBottom = async () => {
  await nextTick();
  if (listElement.value) listElement.value.scrollTop = listElement.value.scrollHeight;
};

// failures are toasted by the API client
const applyTemplate = async (templateKey: FormMessageTemplateKey) => {
  if (props.orderId === null) return;

  isLoadingTemplate.value = true;
  try {
    const preview = await previewMessageTemplate(props.candidateEntityId, templateKey, props.orderId);
    form.value.subject = preview.subject;
    form.value.body = preview.body;
    appliedTemplate.value = preview;
    selectedTemplateKey.value = templateKey;
  } catch (error) {
    console.error('error loading message template:', error);
  } finally {
    isLoadingTemplate.value = false;
  }
};

const onTemplateSelected = (templateKey: FormMessageTemplateKey | null) => {
  if (!templateKey) {
    selectedTemplateKey.value = null;
    appliedTemplate.value = null;
    return;
  }

  if (hasUnsavedEdits.value) {
    pendingTemplateKey.value = templateKey;
    isReplaceConfirmationOpen.value = true;
    return;
  }

  applyTemplate(templateKey);
};

const confirmTemplateReplacement = () => {
  if (pendingTemplateKey.value) applyTemplate(pendingTemplateKey.value);
  pendingTemplateKey.value = null;
};

const loadMessages = async (showSpinner = false) => {
  if (showSpinner) isLoading.value = true;
  try {
    const data = await fetchCandidateMessages(props.candidateEntityId, props.orderId);
    // file request notifications are listed with their requests instead
    messages.value = data.filter((message) => !message.template_key?.startsWith('file_request_'));
  } catch (error) {
    console.error('error loading messages:', error);
  } finally {
    isLoading.value = false;
    await scrollToBottom();
  }
};

const send = async () => {
  if (!canSend.value) return;

  isSending.value = true;
  try {
    const payload: Parameters<typeof sendCandidateMessage>[1] = {
      medium: form.value.medium,
      body: form.value.body,
    };
    if (form.value.medium === 'email' && form.value.subject.trim()) {
      payload.subject = form.value.subject.trim();
    }
    if (props.publicationId !== null) payload.publication_id = props.publicationId;
    if (props.orderId !== null) payload.order_id = props.orderId;

    await sendCandidateMessage(props.candidateEntityId, payload);
    form.value.body = '';
    form.value.subject = '';
    selectedTemplateKey.value = null;
    appliedTemplate.value = null;
    await loadMessages();
  } catch (error) {
    console.error('error sending message:', error);
  } finally {
    isSending.value = false;
  }
};

/* --------------------------------- WATCHERS -------------------------------- */

// a channel the candidate cannot be reached on is not offered, so the form must not stay on it
watch(
  () => props.availableMediums,
  (availableMediums) => {
    form.value.medium = availableMediumOrFirst(form.value.medium, availableMediums);
  },
  { immediate: true }
);

// another order has its own thread and can have another posting language, so an untouched template is fetched again
watch(
  () => props.orderId,
  () => {
    loadMessages(true);
    if (selectedTemplateKey.value && !hasUnsavedEdits.value) applyTemplate(selectedTemplateKey.value);
  }
);

/* ------------------------------ LIFECYCLE HOOKS ---------------------------- */

useCandidateUpdates(
  () => props.candidateEntityId,
  'messages',
  () => loadMessages()
);

onMounted(() => {
  loadMessages(true);
  getSignature()
    .then((userSignature) => {
      signature.value = userSignature;
    })
    .catch((error) => console.error('error loading signature:', error));
});

defineExpose({ refresh: () => loadMessages(), isLoading });
</script>
